import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import classNames from 'classnames';
import { getRatioClassnames } from '@/lib/utils/cn';
import Skeleton from './Skeleton';

const sectionVariants = cva('px-2 xl:px-0 py-8 xl:py-16', {
  variants: {
    size: {
      sm: 'mx-auto max-w-screen-sm',
      md: 'mx-auto max-w-screen-md',
      lg: 'mx-auto max-w-screen-lg',
      xl: 'mx-auto max-w-screen-xl',
    },
    center: {
      true: '',
      false: '',
    },
    variant: {
      '2columns': '',
    },
  },
  defaultVariants: {
    size: 'xl',
    center: true,
  },
});

type SectionProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> &
  VariantProps<typeof sectionVariants> & {
    title?: React.ReactNode;
    subtitle?: React.ReactNode;
    bgColor?: string;
    center?: boolean;
    loading?: boolean;
    topRight?: React.ReactNode;
    ratio?: string;
  };

export function Section({
  title,
  subtitle,
  bgColor,
  className,
  loading,
  center = true,
  children,
  size = 'xl',
  topRight,
  ratio = '2:1',
  variant,
  ...props
}: SectionProps) {
  const [leftClass, rightClass] = getRatioClassnames(ratio);

  if (variant === '2columns') {
    const [leftClass, rightClass] = getRatioClassnames(ratio);

    return (
      <section className={classNames(bgColor, className)} {...props}>
        <div className={classNames(sectionVariants({ size, center: false, variant }))}>
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start">
            <div className={classNames('sticky top-20', leftClass)}>
              {title && (
                <h2 className="mb-4 text-3xl font-extrabold leading-none tracking-tight md:text-4xl">
                  {loading ? <Skeleton className="h-8 w-40" /> : title}
                </h2>
              )}
              {subtitle && <div className="text-gray-600">{loading ? <Skeleton className="mt-5 h-5 w-80" rows={2} /> : subtitle}</div>}
            </div>
            <div className={classNames('space-y-6', rightClass)}>{loading ? <Skeleton className="mt-5 h-10 w-80" /> : children}</div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={classNames(bgColor, className)} {...props}>
      <div className={classNames(sectionVariants({ size, center, variant }))}>
        <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6">
          <div className={classNames('min-w-0', leftClass)}>
            {title && (
              <h2 className={classNames('mb-4 text-3xl font-extrabold leading-none tracking-tight md:text-4xl', { 'text-center': center })}>
                {loading ? <Skeleton className="h-8 w-40" /> : title}
              </h2>
            )}
            {subtitle && (
              <div
                className={classNames('', {
                  'text-center max-w-2xl mx-auto': center,
                })}
              >
                {loading ? <Skeleton className="mt-5 h-5 w-80" rows={2} /> : subtitle}
              </div>
            )}
          </div>
          {topRight && <div className={classNames('mt-4 md:mt-0 md:ml-8 flex-shrink-0', rightClass)}>{topRight}</div>}
        </div>
        {loading ? <Skeleton className="mt-5 h-10 w-80" /> : children}
      </div>
    </section>
  );
}

export default Section;
